import React from "react";
import { Box } from "@mui/material";
import UserInputWithChip from "./UserInputWithChip";
import InputWithTooltip from "./InputWithTooltip";
import { styles } from "./UserInputForm.styles";
import InputAccordion from "./InputAccordion";
import { formDataLimits, checkLimit } from "../../constants/formDataLimits";
import { FONT_SIZES } from "../../constants/fontSizes";

const fieldExplanations = {
  project_description: `Provide a detailed description of your project or business. This will influence the names that are generated (Max ${formDataLimits.project_description} characters)`,
  candidate_names: `Optional:Comma separated list of potential trademark names you're considering. These names will be checked for similarity against existing trademarks. You can also iterate by generating a few rounds of names and keep only what you like. (Max ${formDataLimits.candidate_names} names)`,
  num_names: `Specify how many alternative name suggestions you would like to receive. The system will generate this many additional name options based on your input. The limit is ${formDataLimits.num_names.max} total (candidates + generated) names`,
  industry: `Enter your business's primary industry or sector. This helps in checking for trademark conflicts within your specific market space. (Max ${formDataLimits.industry} characters)`,
};

const TrademarkInputForm = ({
  formData,
  setFormData,
  disabled,
  onClearAllNames,
  onTrademarkCandidatesChange,
}) => {
  const handleProjectDescriptionChange = (e) => {
    const { value } = checkLimit("project_description", e.target.value);
    setFormData((prev) => ({
      ...prev,
      project_description: value,
    }));
  };

  const handleCandidateNamesChange = (newNames) => {
    const { value } = checkLimit("candidate_names", newNames);
    setFormData((prev) => ({
      ...prev,
      candidate_names: value,
    }));
    if (onTrademarkCandidatesChange) {
      onTrademarkCandidatesChange(value);
    }
  };

  const handleIndustryChange = (e) => {
    const { value } = checkLimit("industry", e.target.value);
    setFormData((prev) => ({
      ...prev,
      industry: value,
    }));
  };

  const handleNumNamesChange = (e) => {
    const value = e.target.value;
    if (value === "" || !isNaN(value)) {
      setFormData((prev) => ({
        ...prev,
        num_names: value,
      }));
    }
  };

  const handleNumNamesBlur = (e) => {
    const { value } = checkLimit(
      "num_names",
      e.target.value || 1,
      formData.candidate_names?.length || 0
    );
    setFormData((prev) => ({
      ...prev,
      num_names: value,
    }));
  };
  const title =
    formData?.job_type === "trademark"
      ? "Trademark"
      : formData?.job_type === "full"
      ? "Name Ideation & Trademark"
      : "Name Ideation";

  return (
    <InputAccordion title={title} expanded={formData?.job_type === "trademark"}>
      <Box>
        <InputWithTooltip
          fullWidth
          multiline
          minRows={1}
          maxRows={5}
          label="Project Description"
          name="project_description"
          value={formData.project_description}
          onChange={handleProjectDescriptionChange}
          required
          disabled={disabled}
          size="small"
          sx={{
            ...styles.projectDescription,
            "& .MuiInputLabel-root": { fontSize: FONT_SIZES.TEXT_HINT },
          }}
          tooltipText={fieldExplanations.project_description}
        />
        <InputWithTooltip
          fullWidth
          label="Industry"
          name="industry"
          value={formData.industry}
          onChange={handleIndustryChange}
          required
          disabled={disabled}
          size="small"
          sx={{
            ...styles.textField,
            "& .MuiInputLabel-root": { fontSize: FONT_SIZES.TEXT_HINT },
          }}
          tooltipText={fieldExplanations.industry}
        />
        <UserInputWithChip
          label="Product or Company Candidate Names"
          value={formData.candidate_names || []}
          inputBoxValue={formData.candidate_names_input_box || ""}
          handleInputBoxChange={(inputBoxValue) =>
            setFormData((prev) => ({
              ...prev,
              candidate_names_input_box: inputBoxValue,
            }))
          }
          handleChipChange={handleCandidateNamesChange}
          disabled={disabled}
          tooltipText={fieldExplanations.candidate_names}
          name="candidate_names"
          helperText="Type a name (or comma separated list) and press Enter to add."
          onClearAll={onClearAllNames}
          labelStyle={{ fontSize: FONT_SIZES.TEXT_HINT }}
          helperTextStyle={{ fontSize: FONT_SIZES.TEXT_HINT }}
        />
        <InputWithTooltip
          fullWidth
          type="number"
          label="Nr. of Candidate Names to Generate"
          name="num_names"
          value={formData.num_names}
          onChange={handleNumNamesChange}
          onBlur={handleNumNamesBlur}
          required
          disabled={disabled}
          inputProps={{
            min: formDataLimits.num_names.min,
            max: formDataLimits.num_names.max,
          }}
          size="small"
          tooltipText={fieldExplanations.num_names}
          sx={{
            ...styles.textField,
            mb: 2,
            "& .MuiInputLabel-root": { fontSize: FONT_SIZES.TEXT_HINT },
          }}
        />
      </Box>
    </InputAccordion>
  );
};

export default TrademarkInputForm;
